import { sleep } from 'kitto'

type ScrollTarget = string | HTMLElement | number | undefined

/**
 * Smoothly scrolls to a target element or position with optional delay
 *
 * @param target - The scroll target. Can be:
 *   - A string selector (e.g., '#section', '.class', 'element')
 *   - An HTMLElement instance
 *   - A number representing the scroll position in pixels
 * @param delay - Delay in milliseconds before scrolling (default: 100)
 *
 * @example
 * ```ts
 * // Scroll to element by ID
 * await scrollto('#hero')
 *
 * // Scroll to element by class
 * await scrollto('.section')
 *
 * // Scroll to specific pixel position
 * await scrollto(500)
 *
 * // Scroll with custom delay
 * await scrollto('#footer', 200)
 * ```
 */
export async function scrollto(target: ScrollTarget, delay = 100): Promise<void> {
	if (!target && target !== 0) return
	const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
	await sleep(delay)

	if (typeof target === 'number') {
		window.scrollTo({ top: target, behavior })
		return
	}

	const element = get_element(target)
	if (element) element.scrollIntoView({ behavior })
	else console.warn('Scroll target not found:', target)
}

/**
 * Helper function to find an element from various target types
 *
 * @param target - String selector or HTMLElement
 * @returns The found element or null if not found
 */
function get_element(target: string | HTMLElement): HTMLElement | null {
	if (target instanceof HTMLElement) return target

	// try id selector first, then fallback to any selector
	target = target.startsWith('#') ? target.slice(1) : target
	return document.querySelector(`[id="${target}"]`) ?? document.querySelector(target)
}
